import { useEffect, useState } from 'react';
import { SUBHEADER_STICKY_HEIGHT } from '@/components/SubHeader/SubHeader';

const getSidebarVisibleHeight = (
  isStickyHeader: boolean,
  sidebarRef: React.RefObject<HTMLDivElement>,
  subheaderRef: React.RefObject<HTMLDivElement>,
) => {
  if (!sidebarRef.current) {
    return 0;
  }
  const rect = sidebarRef.current.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;

  const visibleTop = isStickyHeader ? 0 : Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, windowHeight);

  const stickyHeaderHeight = isStickyHeader ? SUBHEADER_STICKY_HEIGHT + (subheaderRef.current?.offsetTop || 0) : 0;

  const visibleHeight = Math.max(0, visibleBottom - visibleTop - stickyHeaderHeight);

  return visibleHeight;
};

const useIsFullSidebarVisible = ({
  isStickyHeader,
  platformShellHeaderHeight,
  footerRef,
  headerRef,
  sidebarRef,
  subHeaderRef,
}: {
  isStickyHeader: boolean;
  platformShellHeaderHeight: number;
  footerRef: React.RefObject<HTMLDivElement>;
  headerRef: React.RefObject<HTMLDivElement>;
  sidebarRef: React.RefObject<HTMLDivElement>;
  subHeaderRef: React.RefObject<HTMLDivElement>;
}) => {
  const [isFullSidebarVisible, setIsFullSidebarVisible] = useState(true);

  useEffect(() => {
    const checkSidebarHeight = () => {
      if (!sidebarRef.current || !headerRef.current || !footerRef.current) {
        return;
      }

      const sidebarEmptyHeaderHeight = isStickyHeader ? 0 : 0;

      setIsFullSidebarVisible(
        getSidebarVisibleHeight(isStickyHeader, sidebarRef, subHeaderRef) >
          headerRef.current.offsetHeight + footerRef.current.offsetHeight + sidebarEmptyHeaderHeight,
      );
    };

    checkSidebarHeight();

    window.addEventListener('resize', checkSidebarHeight);
    if (!isStickyHeader) {
      window.addEventListener('scroll', checkSidebarHeight);
    }

    return () => {
      window.removeEventListener('resize', checkSidebarHeight);
      window.removeEventListener('scroll', checkSidebarHeight);
    };
  }, [footerRef, headerRef, isStickyHeader, platformShellHeaderHeight, sidebarRef, subHeaderRef]);

  return {
    isFullSidebarVisible,
    footerRef,
    headerRef,
    sidebarRef,
  };
};

export default useIsFullSidebarVisible;
